import { PureComponent, createRef } from "react";

import _ from "lodash";

import { colorsSets } from "@similarweb/styles";
import { PlainTooltip, Switcher } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { ExcelButton } from "app/components/React/ExcelButton/ExcelButton";
import { NoPrintContainer, WeShare } from "app/components/weshare/weshare";
import { getShouldHideSegments } from "app/components/widget/widget-config/metrics/segments/utils";
import type { IWidgetModelTypesType } from "app/components/widget/widget-types/Widget";
import { SEGMENTS_COMPARISON_METRICS } from "app/components/widget/widget-utilities/segments/constants";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import type { ICustomSegment, ICustomSegmentGroupWebsite } from "app/services/segments/segmentsApiService";
import { SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { allTrackers } from "app/services/track/track";

import BoxSubtitle from "../../../../components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "../../../../components/BoxTitle/src/BoxTitle";
import Chart from "../../../../components/Chart/src/Chart";
import "../../../../components/Chart/styles/sharedTooltip.scss";
import { GraphLoader } from "../../../../components/Loaders/src/ExpandedTableRowLoader/ExpandedTableRowLoader";
import { NoData } from "../../../../components/NoData/src/NoData";
import StyledBoxSubtitle from "../../../../styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { FlexColumn, FlexRow } from "../../../../styled components/StyledFlex/src/StyledFlex";

import { benchmarkVerticals } from "./benchmarkVerticals";
import { getChartConfig, transformData } from "./SegmentsVsGroupLineChart";
import {
    BoxContainer,
    Bullet,
    ChartContainer,
    ContentContainer,
    ExcelButtonContainer,
    GraphContainer,
    LabelOverflowHidden,
    LegendContainer,
    LegendTitle,
    NoDataContainer,
    SegmentNameLabel,
    SitesChartLoaderContainer,
    StyledHeaderTitle,
    StyledLegendContainer,
    TabsContainer,
    TitleContainer,
    UtilitiesContainer,
    Disabled,
    StyleTabSwitchItem,
} from "./StyledComponents";

import { SegmentTypeBadge } from "pro-features/pages/segments/StyledComponents";
import DurationService from "app/services/DurationService";
import CountryService from "app/services/CountryService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { US_COUNTRY_ID } from "app/services/countryServiceConst";
import type {
    IBenchmarkOvertimeProps,
    IBenchmarkOvertimeState,
    IBenchmarkVertical,
} from "pro-features/pages/segments/components/benchmarkOvertime/types";
import { segmentTypesDisabledVerticals } from "pro-features/pages/segments/components/benchmarkOvertime/types";

export class BenchmarkOvertime extends PureComponent<IBenchmarkOvertimeProps, IBenchmarkOvertimeState> {
    protected translate;

    protected swNavigator;

    private chartRef;

    constructor(props: IBenchmarkOvertimeProps) {
        super(props);
        this.chartRef = createRef();

        this.state = {
            selectedTabIndex: 0,
        };
        this.translate = i18nFilter();
        this.swNavigator = proNavigator;
        this.renderLegend = this.renderLegend.bind(this);
        this.onTabClick = this.onTabClick.bind(this);
    }

    private isTabDisabled = (tab: IBenchmarkVertical) => {
        const { duration, webSource, country } = this.swNavigator.getParams();
        const { supportedWebSources, unsupportedDurations = [] } = tab;
        // @ts-ignore FIXME: strict migration - 'supportedWebSources' possibly undefined — every entry in benchmarkVerticals defines supportedWebSources
        const isDisabledDueToWebSource = !supportedWebSources.includes(webSource);
        const isDisabledDueToDuration = unsupportedDurations.includes(duration);
        const isEnabledDueToUS = Number(country) === US_COUNTRY_ID && webSource !== devicesTypes.TOTAL;

        if (isEnabledDueToUS) {
            return false;
        }

        return isDisabledDueToDuration || isDisabledDueToWebSource;
    };

    public onTabClick(value: number) {
        const verticalKey = Object.keys(benchmarkVerticals)[value];
        const tab = benchmarkVerticals[verticalKey];
        if (this.isTabDisabled(tab)) {
            return;
        }
        allTrackers.trackEvent(
            "Metric Button",
            "click",
            `${this.translate(this.props.title)}/Change Tab/${benchmarkVerticals?.[verticalKey]?.name}`,
        );
        this.props.setSelectedTab(verticalKey);
        this.setState({
            selectedTabIndex: value,
        });
    }

    public renderChart(selectedTab: FixmeType, filteredChartData: FixmeType, selectedRows: FixmeType) {
        const { rowSelectionProp, availableMembers, selectedGranularity } = this.props;

        const { duration } = this.swNavigator.getParams();
        const durationApi = DurationService.getDurationData(duration);

        const transformedData = transformData(
            filteredChartData,
            selectedRows,
            // @ts-ignore FIXME: strict migration - 'rowSelectionProp' is optional (string | undefined) on props but transformData requires string; always provided by callers
            rowSelectionProp,
            availableMembers,
            selectedGranularity,
            durationApi,
        ).sort((a, b) => a.rowIndex - b.rowIndex);

        const sanitizedData = transformedData.map((data) => {
            const { seriesSubtitle, ...rest } = data;
            return rest;
        });
        const chart = this.props.renderChart ? (
            this.props.renderChart(selectedTab, filteredChartData, selectedRows)
        ) : (
            <ChartContainer>
                <Chart
                    key={selectedTab.name}
                    type={"line"}
                    config={getChartConfig({
                        type: "line",
                        height: 310,
                        metric: selectedTab,
                        filter: selectedTab.filter,
                        data: transformedData,
                        timeGranularity: selectedGranularity,
                        isWindow: durationApi?.forAPI?.isWindow,
                        toDateMoment: durationApi?.raw?.to,
                        durationObject: durationApi,
                        invertTooltipColor: selectedTab.invertChange,
                    })}
                    data={sanitizedData}
                    // isFinalConfig={true}
                />
            </ChartContainer>
        );
        return chart;
    }

    public renderLegend(selectedChartData: FixmeType, selectedTab: FixmeType) {
        const chartLegend = this.props.renderLegend
            ? this.props.renderLegend(selectedChartData, selectedTab)
            : this._renderLegend(selectedChartData, selectedTab);
        return chartLegend;
    }

    public _renderLegend(selectedChartData: FixmeType, selectedTab: FixmeType) {
        const { selectedRows, rowSelectionProp, availableMembers } = this.props;
        const legendItemsColors = selectedRows
            ? _.map(selectedRows, (row: any) => ({
                  // @ts-ignore FIXME: strict migration - 'rowSelectionProp' possibly undefined used as index; always provided by callers
                  chartSegmentId: row[rowSelectionProp],
                  chartSegmentColor: row.selectionColor,
              }))
            : _.map(Object.keys(selectedChartData), (chartSegmentId: string, index) => ({
                  chartSegmentId,
                  chartSegmentColor: colorsSets.c.toArray()[index],
              }));
        const legendItems = _.map(legendItemsColors, ({ chartSegmentId, chartSegmentColor }) => {
            const [customSegment, segmentType] = SegmentsUtils.getSegmentObjectByKey(chartSegmentId, availableMembers);
            if (!customSegment) {
                return null;
            }
            let segmentNameComp;
            let segmentTypeName;
            switch (segmentType) {
                case SEGMENT_TYPES.SEGMENT:
                    const segmentObj = customSegment as ICustomSegment;
                    segmentNameComp = this._renderLegendItemSegment(segmentObj.domain, segmentObj.segmentName);
                    break;
                case SEGMENT_TYPES.WEBSITE:
                    const websiteObj = customSegment as ICustomSegmentGroupWebsite;
                    segmentTypeName = this.translate("segments.group.analysis.legend.website");
                    segmentNameComp = this._renderLegendItemWebsite(websiteObj.domain, segmentTypeName);
                    break;
            }
            const isDisabled = segmentTypesDisabledVerticals[segmentType]?.includes(selectedTab.dataKey);
            const legendItem = (
                <LegendContainer
                    key={`${chartSegmentId}`}
                    isDisabled={isDisabled}
                >
                    <LegendTitle>
                        <Bullet color={chartSegmentColor} />
                        <LabelOverflowHidden>{customSegment.domain}</LabelOverflowHidden>
                    </LegendTitle>
                    {segmentNameComp}
                </LegendContainer>
            );
            if (isDisabled) {
                const disabledTooltipContent = (
                    <>
                        <div>
                            {this.translate("segments.group.analysis.legend.vertical.disabled.part1", {
                                segmentType: segmentTypeName?.toLowerCase(),
                            })}
                            ,
                        </div>
                        <div>
                            {this.translate("segments.group.analysis.legend.vertical.disabled.part2", {
                                verticalName: this.translate(selectedTab.title)?.toLowerCase(),
                            })}
                            .
                        </div>
                    </>
                );
                return (
                    <PlainTooltip
                        placement="top"
                        tooltipContent={disabledTooltipContent}
                    >
                        {legendItem}
                    </PlainTooltip>
                );
            }
            return legendItem;
        });

        return <StyledLegendContainer>{legendItems}</StyledLegendContainer>;
    }

    private _renderLegendItemSegment(segmentDomain: string, segmentName: string) {
        return (
            <PlainTooltip
                placement={"bottom"}
                tooltipContent={`${segmentDomain} - ${segmentName}`}
            >
                <SegmentNameLabel>{segmentName}</SegmentNameLabel>
            </PlainTooltip>
        );
    }

    private _renderLegendItemWebsite(websiteDomain: string, websiteTypeDescription: string) {
        return (
            <PlainTooltip
                placement={"bottom"}
                tooltipContent={`${websiteDomain} - ${websiteTypeDescription}`}
            >
                <SegmentNameLabel>
                    <SegmentTypeBadge>WEBSITE</SegmentTypeBadge>
                </SegmentNameLabel>
            </PlainTooltip>
        );
    }

    public getAddToDashboardButton = () => {
        const { selectedGranularity, selectedRows, filters } = this.props;
        const selectedTab = Object.values(benchmarkVerticals)[this.state.selectedTabIndex];
        // @ts-ignore FIXME: strict migration - 'selectedRows' possibly undefined — getAddToDashboardButton only rendered when selectedChartData (and selectedRows) is present
        const selectedSegmentsId = selectedRows.map((selectedSegments) => selectedSegments.SegmentId);
        // @ts-ignore FIXME: strict migration - selectedTab.name is a dynamic string key not present in SEGMENTS_COMPARISON_METRICS' typed key set
        const selectedMetricConfig = SEGMENTS_COMPARISON_METRICS?.[selectedTab.name];

        return (
            <AddToDashboard
                metric={selectedMetricConfig?.name}
                type={selectedMetricConfig?.type as IWidgetModelTypesType}
                webSource={filters.webSource}
                filters={{
                    timeGranularity: selectedGranularity,
                    segmentsGroupId: filters.id,
                    selectedMetricName: selectedTab.dataKey,
                    selectedSegmentsId,
                }}
                modelType="fromSegment"
            />
        );
    };

    public renderTabs() {
        const { filters } = this.props;
        const comingSoon = translate("common.coming.soon");

        return Object.values(benchmarkVerticals).map((vertical: IBenchmarkVertical) => {
            const isDisabledDueToWebSource =
                // @ts-ignore FIXME: strict migration - 'supportedWebSources' possibly undefined — every entry in benchmarkVerticals defines supportedWebSources
                !vertical.supportedWebSources.includes(filters.webSource) && filters.webSource === devicesTypes.TOTAL;
            const disabled = this.isTabDisabled(vertical);

            return (
                <StyleTabSwitchItem
                    key={vertical.dataKey}
                    disabled={disabled}
                >
                    <PlainTooltip
                        placement={"top"}
                        tooltipContent={this.translate(isDisabledDueToWebSource ? comingSoon : vertical.tooltipInfo)}
                    >
                        {disabled ? (
                            <Disabled>{this.translate(vertical.title)}</Disabled>
                        ) : (
                            <span>{this.translate(vertical.title)}</span>
                        )}
                    </PlainTooltip>
                </StyleTabSwitchItem>
            );
        });
    }

    public render() {
        const { title, titleTooltip, filters, data, selectedRows, isLoading, rowSelectionProp, SwitcherComponent } =
            this.props;
        const { selectedTabIndex } = this.state;
        const params = this.swNavigator.getParams();
        // @ts-ignore FIXME: strict migration - StateService.current().name is string | undefined but getStateUrl expects StateOrName; a route is always active here
        const redirectUrl = this.swNavigator.getStateUrl(this.swNavigator.current().name, params);

        const subtitleFilters = [
            {
                filter: "date",
                value: {
                    from: filters.from,
                    to: filters.to,
                },
            },
            {
                filter: "country",
                countryCode: filters?.country,
                value: CountryService.getCountryById(filters?.country).text,
            },
            {
                filter: "webSource",
                value: filters.webSource,
            },
        ];
        const selectedTab = Object.values(benchmarkVerticals)[selectedTabIndex];
        const selectedChartData = data && data.Data ? data.Data[selectedTab.dataKey] : undefined;

        const filteredChartData =
            selectedChartData && selectedRows
                ? Object.keys(selectedChartData)
                      // @ts-ignore FIXME: strict migration - 'rowSelectionProp' possibly undefined used as index; always provided by callers
                      .filter((chartDomain) => _.find(selectedRows, (row) => row[rowSelectionProp] === chartDomain))
                      .reduce((obj, key) => {
                          // @ts-ignore FIXME: strict migration - reduce accumulator inferred as '{}', string index assignment not allowed
                          obj[key] = selectedChartData[key];
                          return obj;
                      }, {})
                : selectedChartData;

        const comingSoon = translate("common.coming.soon");
        const shouldHideSegments = getShouldHideSegments();

        return (
            <BoxContainer
                data-automation-sites-vs-category={true}
                ref={this.chartRef}
            >
                <TitleContainer>
                    <FlexColumn>
                        <StyledHeaderTitle>
                            <BoxTitle tooltip={this.translate(titleTooltip)}>{this.translate(title)}</BoxTitle>
                        </StyledHeaderTitle>
                        <StyledBoxSubtitle>
                            <BoxSubtitle filters={subtitleFilters} />
                        </StyledBoxSubtitle>
                    </FlexColumn>
                    <NoPrintContainer>
                        <FlexRow>
                            <SwitcherComponent />
                            {this.props.tableExcelLink ? (
                                <ExcelButtonContainer>
                                    <ExcelButton
                                        url={this.props.tableExcelLink}
                                        trackName="segment group"
                                    />
                                </ExcelButtonContainer>
                            ) : null}
                            <WeShare
                                chartRef={this.chartRef.current}
                                title={this.translate(title)}
                                description={this.translate(selectedTab.title)}
                                redirectUrl={`${swSettings.swsites.pro}/${redirectUrl}`}
                            />
                        </FlexRow>
                    </NoPrintContainer>
                </TitleContainer>
                <ContentContainer>
                    <TabsContainer>
                        <NoPrintContainer>
                            <Switcher
                                selectedIndex={selectedTabIndex}
                                className={"SegmentsVsGroup"}
                                onItemClick={this.onTabClick}
                                isCarousal={true}
                            >
                                {this.renderTabs()}
                            </Switcher>
                        </NoPrintContainer>
                    </TabsContainer>
                    {isLoading ? (
                        <SitesChartLoaderContainer>
                            <GraphLoader width={"100%"} />
                        </SitesChartLoaderContainer>
                    ) : (
                        <GraphContainer className={"sharedTooltip"}>
                            {selectedChartData ? (
                                <>
                                    <UtilitiesContainer>
                                        {this.renderLegend(selectedChartData, selectedTab)}
                                        {!shouldHideSegments ? this.getAddToDashboardButton() : null}
                                    </UtilitiesContainer>
                                    {this.renderChart(selectedTab, filteredChartData, selectedRows)}
                                </>
                            ) : (
                                <NoDataContainer>
                                    {" "}
                                    <NoData />{" "}
                                </NoDataContainer>
                            )}
                        </GraphContainer>
                    )}
                </ContentContainer>
            </BoxContainer>
        );
    }
}
